import { format, type Locale } from "date-fns";
import type { TFunction } from "i18next";
import * as z from "zod";
import { weekdayKeys } from "../schedule-time/schedule-dates";
import { ScheduleDocumentSchema, type ScheduleDocument } from "./schedule-json";
import { slotFields, slotPromptValues } from "./slot-fields";

// Built once. Descriptions on the schema carry only the rules `.refine()` drops.
const { $schema: _dialect, ...schemaBody } = z.toJSONSchema(ScheduleDocumentSchema);
const scheduleJsonSchema = JSON.stringify(schemaBody);

const slotColumns = ["slot", ...slotFields.map(field => field.column)].join(" | ");

function describeCurrentSchedule(schedule: ScheduleDocument) {
  const slotLines = weekdayKeys.flatMap((weekday) =>
    [1, 2].flatMap((number) => {
      const slot = schedule.slots[`${weekday}-${number}`];
      if (!slot) return [];
      const isEmpty = !slot.active && slot.title === "" && slot.subheading === "";
      if (isEmpty) return [];
      const values = slotPromptValues(slot);
      return [[`${weekday}-${number}`, ...values].join(" | ")];
    }),
  );

  const offlineDates = Object.keys(schedule.offlineMessages).sort();
  const offlineLines = offlineDates.flatMap((date) => {
    const message = schedule.offlineMessages[date] ?? "";
    const active = schedule.offlineActive[date] ?? false;
    if (message === "" && !active) return [];
    return [`offline ${date} | ${active ? "on" : "off"} | ${message}`];
  });

  const commonOptions = `commonOptions: ${JSON.stringify(schedule.commonOptions)}`;
  return [commonOptions, slotColumns, ...slotLines, ...offlineLines].join("\n");
}

export type SchedulePromptInput = {
  request: string;
  today: Date;
  weekStart: Date;
  timezone: string;
  currentSchedule?: ScheduleDocument;
  dateLocale: Locale;
};

export function buildSchedulePrompt(input: SchedulePromptInput, t: TFunction) {
  const dateLocale = input.dateLocale;
  const todayText = format(input.today, "yyyy-MM-dd (EEEE)", { locale: dateLocale });
  const weekStartText = format(input.weekStart, "yyyy-MM-dd");
  const trimmedRequest = input.request.trim();
  const request = trimmedRequest || t("ai.prompt.defaultRequest");

  const lines = [
    scheduleJsonSchema,
    t("ai.prompt.today", { date: todayText }),
    t("ai.prompt.week", { date: weekStartText }),
    t("ai.prompt.timezone", { timezone: input.timezone }),
  ];

  if (input.currentSchedule) {
    const currentText = describeCurrentSchedule(input.currentSchedule);
    lines.push("", t("ai.prompt.current"), currentText);
  }

  lines.push("", t("ai.prompt.request"), request);
  return lines.join("\n");
}
